import brand from '@helpers/brand';
import { fluidAdjusted } from '@helpers/fluid';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { gradientStyle, headingStyles, paragraphLarge } from '@helpers/typography';
import BurnEffect from '@img/burn-effect.png';
import HexagonSvg from '@img/shapes/hexagon-solid.svg';
import TopLeftSvg from '@img/shapes/shape-bottom-right.svg';
import BottomRightSvg from '@img/shapes/shape-group.svg';
import { ButtonStyles } from '@stories/Components/Buttons/GeneralButton/Button.styles';
import SectionWrapperStyles from '@stories/Components/Global/SectionWrapper/SectionWrapper.styles';
import VideoModalStyles from '@stories/Components/Global/VideoModal/VideoModal.styles';
import { rgba } from 'polished';
import styled from 'styled-components';
const Container = styled.section `
  --bgColor: ${brand.theme.orange};
  overflow: hidden;

  background-color: var(--bgColor);
  position: relative;
  display: block;
  min-height: 100vh;

  &[data-theme='blue'] {
    --bgColor: ${brand.theme.blue};
  }

  &[data-theme='pink'] {
    --bgColor: ${brand.theme.pink};
  }

  &[data-theme='green'] {
    --bgColor: ${brand.theme.green};
  }

  ${SectionWrapperStyles.Container} {
    --sectionSpacingTop: 80px;
    --sectionSpacingBottom: 0;

    @media ${from(Device.Tablet)} {
      --sectionSpacingTop: 150px;
      --sectionSpacingBottom: 100px;
    }

    @media ${from(Device.DesktopLarge)} {
      --sectionSpacingTop: 200px;
    }
  }

  @media ${until(Device.Tablet)} {
    min-height: 100vh;
    min-height: 100dvh;
  }

  @media ${from(Device.Tablet)} {
    min-height: 70vh;
  }

  @media ${from(Device.TabletExtraLarge)} {
    min-height: 100vh;
  }

  ${SectionWrapperStyles.Container} {
    @media ${until(Device.Tablet)} {
      min-height: calc(100vh - var(--sectionSpacingTop));
      min-height: calc(100dvh - var(--sectionSpacingTop));
    }
  }

  ${SectionWrapperStyles.Inner} {
    @media ${until(Device.Tablet)} {
      min-height: calc(100vh - var(--sectionSpacingTop));
      min-height: calc(100dvh - var(--sectionSpacingTop));
    }
  }

  ${SectionWrapperStyles.SiteWide} {
    @media ${until(Device.Tablet)} {
      min-height: calc(100vh - var(--sectionSpacingTop));
      min-height: calc(100dvh - var(--sectionSpacingTop));
    }
  }

  ${VideoModalStyles.Container} {
    --sectionSpacingTop: 60px;
    --sectionSpacingBottom: 60px;

    @media ${until(Device.Tablet)} {
      min-height: 0;

      ${SectionWrapperStyles.Inner},
      ${SectionWrapperStyles.SiteWide} {
        min-height: 0;
      }
    }
  }
`;
const ShapeTopLeft = styled(TopLeftSvg) `
  --color: ${rgba(brand.buff.buff4, 0.62)};
  color: var(--color);

  position: absolute;
  top: 0;
  left: 0;
  height: 40%;
  z-index: 1;

  @media ${until(Device.Tablet)} {
    height: 25%;
  }

  [data-theme='blue'] & {
    --color: ${rgba(brand.tertiary.blue, 0.22)};
  }

  [data-theme='pink'] & {
    --color: ${rgba(brand.tertiary.pink, 0.22)};
  }

  [data-theme='green'] & {
    --color: ${rgba(brand.tertiary.green, 0.22)};
  }
`;
const ShapeBottomRight = styled(BottomRightSvg) `
  --color: ${rgba(brand.buff.buff4, 0.42)};
  color: var(--color);

  position: absolute;
  bottom: 0;
  right: -20px;
  height: 100%;
  z-index: 1;

  @media ${until(Device.Tablet)} {
    height: auto;
    width: 100%;
  }

  [data-theme='blue'] & {
    --color: ${rgba(brand.tertiary.blue, 0.12)};
  }

  [data-theme='pink'] & {
    --color: ${rgba(brand.tertiary.pink, 0.12)};
  }

  [data-theme='green'] & {
    --color: ${rgba(brand.tertiary.green, 0.12)};
  }
`;
const Grid = styled.div `
  ${baseGrid};
  position: relative;
  height: 100%;
  z-index: 2;

  @media ${until(Device.Tablet)} {
    height: auto;
  }
`;
const ContentArea = styled.div `
  grid-column: span 12;
  align-self: flex-end;

  @media ${from(Device.Tablet)} {
    grid-column: span 6;
  }
`;
const Title = styled.h1 `
  ${headingStyles};
  --titleColor: ${brand.secondary.red5};
  font-size: ${fluidAdjusted(54, 96)};
  line-height: ${fluidAdjusted(70, 116)};
  color: var(--titleColor);

  &[data-title-theme='mid'] {
    --titleColor: ${brand.secondary.red3};
  }

  &[data-title-theme='light'] {
    --titleColor: ${brand.secondary.red1};
  }

  &[data-title-theme='gradient'] {
    ${gradientStyle};
  }
`;
const Intro = styled.div `
  ${paragraphLarge};
  max-width: 700px;
`;
const CtaArea = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: ${fluidAdjusted(10, 35)};
  padding-top: 20px;

  @media ${until(Device.MobileLarge)} {
    padding-top: 6px;

    ${ButtonStyles.Button} {
      --buttonHeight: 51px;
      --fontFamily: sans-serif;
    }
  }
`;
const MediaArea = styled.div `
  grid-column: span 12;
  display: grid;
  align-self: end;
  transform: translateY(10%);

  @media ${from(Device.Tablet)} {
    grid-column: 7 / span 6;
    align-self: center;
    justify-content: end;
    transform: translateY(0);
  }
`;
const MediaHolder = styled.div `
  max-width: 540px;
  transform: translateX(25%);

  @media ${from(Device.Tablet)} {
    transform: translateX(0);
  }

  @media ${from(Device.DesktopSmall)} {
    margin-right: 40px;
  }

  @media ${from(Device.DesktopLarge)} {
    max-width: 660px;
  }
`;
const Image = styled.img `
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;
const BurnOverlay = styled.div `
  width: 150%;
  height: 120%;
  background-image: url(${BurnEffect});
  background-position: center;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  mix-blend-mode: lighten;

  @media ${until(Device.TabletSmall)} {
    left: 15%;
  }
`;
const ShapeShadow = styled(HexagonSvg) `
  position: absolute;
  color: ${brand.white};
  transform: translate(12px, 20px);

  @media ${until(Device.Tablet)} {
    display: none;
  }
`;
const SocialArea = styled.div `
  position: absolute;
  bottom: 60px;
  left: 0;

  @media ${from(Device.Tablet)} {
    bottom: auto;
    top: calc(100% + 30px);
    left: auto;
    right: 0;
    width: 100%;
    max-width: 660px;
  }

  @media ${from(Device.DesktopSmall)} {
    top: 50%;
    right: -36px;
    transform: translateY(-50%);
    width: auto;
  }

  @media ${from(Device.DesktopLarge)} {
    left: 100%;
    right: auto;
  }
`;
const SocialList = styled.div `
  display: flex;
  flex-wrap: nowrap;
  flex-direction: column;
  justify-content: center;
  gap: 5px;

  @media ${from(Device.Tablet)} {
    justify-content: center;
    flex-direction: row;
  }

  @media ${from(Device.DesktopSmall)} {
    flex-direction: column;
  }
`;
const SocialLink = styled.a `
  text-decoration: none;
  color: ${brand.secondary.red5};
  transition: opacity 0.3s;
  background-color: ${brand.buff.buff3};
  border-radius: 100000px;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    opacity: 0.6;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  svg {
    height: 16px;
    max-width: 16px;
  }
`;
const PrimaryHeroBannerStyles = {
    BurnOverlay,
    Container,
    ContentArea,
    CtaArea,
    Grid,
    Image,
    Intro,
    MediaArea,
    MediaHolder,
    ShapeBottomRight,
    ShapeShadow,
    ShapeTopLeft,
    SocialArea,
    SocialLink,
    SocialList,
    Title,
};
export default PrimaryHeroBannerStyles;
